import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import Header from '../components/Header';
import OrderSummaryCard from '../components/OrderSummaryCard';
import StripePaymentSection from '../components/StripePaymentSection';
import MockCardFields from '../components/MockCardFields';
import { getTier, formatUsd, calculateOrderTotal } from '../config/tiers';
import { createCheckoutSession } from '../utils/api';
import { trackEvent, getStoredUtms } from '../utils/tracking';
import './Checkout.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const isRealStripeKey = typeof STRIPE_KEY === 'string' && STRIPE_KEY.startsWith('pk_');
const stripePromise = isRealStripeKey ? loadStripe(STRIPE_KEY) : null;

export default function Checkout() {
  const navigate = useNavigate();
  const stripeSectionRef = useRef(null);

  const selection = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('tp_selection'));
    } catch {
      return null;
    }
  }, []);

  const [email, setEmail] = useState(selection?.token?.email || '');
  const [emailError, setEmailError] = useState(null);
  const [session, setSession] = useState(null); // { orderId, clientSecret, paymentIntentId, mock }
  const [submitting, setSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  useEffect(() => {
    if (!selection) navigate('/');
  }, [selection, navigate]);

  if (!selection) return null;

  const tier = getTier(selection.tier);
  const quantity = selection.quantity;
  const { totalCents } = calculateOrderTotal(tier, quantity);

  const handleContinueToPayment = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setEmailError("That email doesn't look quite right.");
      return;
    }
    setEmailError(null);
    setSubmitting(true);
    setPaymentError(null);
    try {
      const result = await createCheckoutSession({
        tier: tier.key,
        quantity,
        email,
        policyId: selection.token?.policyId,
        departureDate: selection.token?.departureDate,
        utms: getStoredUtms(),
      });
      setSession(result);
    } catch (err) {
      setPaymentError("Something hiccuped on our end — your card wasn't charged. Try again?");
      trackEvent('Error Occurred', { step: 'checkout-session', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePay = async () => {
    setSubmitting(true);
    setPaymentError(null);
    try {
      if (session.mock) {
        await fetch(`${process.env.REACT_APP_API_BASE || '/api'}/dev-simulate-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId: session.orderId,
            paymentIntentId: session.paymentIntentId,
            email,
            tier: tier.key,
            quantity,
            amountCents: session.amountCents,
            policyId: selection.token?.policyId,
            departureDate: selection.token?.departureDate,
            utms: getStoredUtms(),
          }),
        });
        navigate(`/confirmation?order=${session.orderId}`);
        return;
      }

      const returnUrl = `${window.location.origin}/confirmation?order=${session.orderId}`;
      const result = await stripeSectionRef.current.confirm(returnUrl);
      if (result.error) {
        setPaymentError("Something hiccuped on our end — your card wasn't charged. Try again?");
        trackEvent('Error Occurred', { step: 'payment', message: result.error.message });
      } else {
        navigate(`/confirmation?order=${session.orderId}`);
      }
    } catch (err) {
      setPaymentError("Something hiccuped on our end — your card wasn't charged. Try again?");
      trackEvent('Error Occurred', { step: 'payment', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tp-checkout">
      <Header />
      <div className="container tp-checkout__content">
        <OrderSummaryCard tier={tier} quantity={quantity} />

        <div className="card tp-checkout__form">
          <div className="tp-checkout__field">
            <label className="label-m" htmlFor="tp-email">Where should we send everything?</label>
            <input
              id="tp-email"
              type="email"
              className="input tp-checkout__email"
              value={email}
              disabled={!!session}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
            <p className="body-s tp-checkout__helper">Your eSIM and lounge pass will land here.</p>
            {emailError && <p className="body-s tp-checkout__error">{emailError}</p>}
          </div>

          {!session ? (
            <button
              type="button"
              className="btn btn-primary tp-checkout__submit"
              onClick={handleContinueToPayment}
              disabled={submitting}
            >
              {submitting ? 'One sec…' : 'Continue to payment'}
            </button>
          ) : (
            <>
              {session.mock || !isRealStripeKey ? (
                <MockCardFields />
              ) : (
                <StripePaymentSection
                  ref={stripeSectionRef}
                  stripePromise={stripePromise}
                  clientSecret={session.clientSecret}
                />
              )}

              {paymentError && <p className="body-s tp-checkout__error">{paymentError}</p>}

              <button
                type="button"
                className="btn btn-primary tp-checkout__submit"
                onClick={handlePay}
                disabled={submitting}
              >
                {submitting ? 'Processing…' : `Pay ${formatUsd(totalCents)}`}
              </button>
              <p className="body-s tp-checkout__reassurance">
                14-day refund if nothing's been redeemed. Instant delivery by email.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
